import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import DataTable, { DataTableEmptyState } from "common/Components/DataTable";
import StatusPill from "common/Components/StatusPill";
import { GenericFormField } from "common/Components/GenericForm";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import {
  CUSTOMER_USERS_ACCESS_STATUS_FILTER_OPTIONS,
  DEFAULT_CUSTOMER_USERS_LIST_FILTERS,
  USERS_PAGE_CONSTANTS,
  buildUsersResultSummary,
  buildRowRoleDropdownOptions,
  deriveCustomerUserAccessVariant,
  formatDisplayPhone,
  getAccountStatusTone,
  getCustomerUserListRowAction,
  getLockedRolesForSelection,
  getMembershipStatusTone,
  isCustomerUserRolesEditable,
  mapCustomerUsersListFiltersToColumnFilters,
  mergeRolesWithLockedRoles,
  normalizeAccountStatusDisplay,
  validateCustomerUserRolesSelection,
} from "../../Users.constants";
import {
  AccessActionButton,
  ClearFiltersButton,
  EmailCellText,
  MonoCellText,
  MutedCellText,
  ResultsBar,
  ResultsSummary,
  UsersListLayout,
} from "./UsersList.styles";

const displayCell = (value) => {
  const text = String(value ?? "").trim();
  return text || "—";
};

const hasActiveCustomerFilters = (filters) =>
  Object.keys(DEFAULT_CUSTOMER_USERS_LIST_FILTERS).some(
    (key) => (filters?.[key] ?? "") !== ""
  );

const roleStringsFromUser = (user) =>
  (Array.isArray(user?.roles) ? user.roles : []).map((r) => String(r));

const rolesSelectionEqual = (left, right) => {
  const a = [...(left ?? [])].map(String).sort();
  const b = [...(right ?? [])].map(String).sort();
  return a.length === b.length && a.every((value, index) => value === b[index]);
};

const CustomerUserRolesField = ({
  row,
  grantableOptions,
  roleLabelMap,
  onAssignRoles,
}) => {
  const selectedRolesRef = useRef(roleStringsFromUser(row.sourceUser));
  const [selectedRoles, setSelectedRoles] = useState(() =>
    roleStringsFromUser(row.sourceUser)
  );
  const [roleError, setRoleError] = useState(null);

  useEffect(() => {
    const next = roleStringsFromUser(row.sourceUser);
    setSelectedRoles(next);
    selectedRolesRef.current = next;
    setRoleError(null);
  }, [row.sourceUser]);

  useEffect(() => {
    selectedRolesRef.current = selectedRoles;
  }, [selectedRoles]);

  const baselineRoles = useMemo(
    () => roleStringsFromUser(row.sourceUser),
    [row.sourceUser]
  );
  const lockedRoles = useMemo(
    () => getLockedRolesForSelection(baselineRoles, grantableOptions),
    [baselineRoles, grantableOptions]
  );
  const dropdownOptions = useMemo(
    () =>
      buildRowRoleDropdownOptions(grantableOptions, lockedRoles, roleLabelMap),
    [grantableOptions, lockedRoles, roleLabelMap]
  );
  const rolesEditable = isCustomerUserRolesEditable(row.sourceUser);
  const hasRoleChoices =
    grantableOptions.length > 0 || lockedRoles.length > 0;

  const commitRolesOnClose = useCallback(async () => {
    if (!rolesEditable) {
      return;
    }
    const pending = selectedRolesRef.current;
    const baseline = roleStringsFromUser(row.sourceUser);
    const rolesError = validateCustomerUserRolesSelection(pending);
    if (rolesError) {
      setRoleError(rolesError);
      setSelectedRoles(baseline);
      selectedRolesRef.current = baseline;
      return;
    }
    setRoleError(null);
    if (rolesSelectionEqual(pending, baseline)) {
      return;
    }
    await onAssignRoles(row.sourceUser, pending);
  }, [onAssignRoles, rolesEditable, row.sourceUser]);

  return (
    <GenericFormField
      field={{
        name: "roles",
        label: "Roles",
        type: "multiSelect",
        options: dropdownOptions,
        compact: true,
        inline: true,
        valueDisplay: "comma",
        disabled: !hasRoleChoices || !rolesEditable,
        emptyLabel: "No roles",
        menuTitle: "Roles",
        roleLabelMap,
        stopClickPropagation: true,
        onOpen: () => {
          const next = roleStringsFromUser(row.sourceUser);
          setSelectedRoles(next);
          selectedRolesRef.current = next;
        },
        onClose: commitRolesOnClose,
      }}
      value={selectedRoles}
      error={roleError}
      onChange={(_name, next) => {
        const merged = mergeRolesWithLockedRoles(next, lockedRoles);
        setSelectedRoles(merged);
        selectedRolesRef.current = merged;
        if (merged.length > 0) {
          setRoleError(null);
        }
      }}
    />
  );
};

CustomerUserRolesField.propTypes = {
  row: PropTypes.object.isRequired,
  grantableOptions: PropTypes.arrayOf(PropTypes.object),
  roleLabelMap: PropTypes.object,
  onAssignRoles: PropTypes.func.isRequired,
};

const UsersList = ({
  customerId,
  users,
  loading = false,
  pagination = {},
  listFilters,
  onClearFilters,
  onListFiltersChange,
  fetchUsers,
  roleOptions,
  roleLabelMap = {},
  onAssignRoles,
  onPatchCustomerAccess,
  onRowAction,
}) => {
  const { customerUsersTableHeaders: headers } = INFRAMARK_CONSTANTS.users;
  const usersList = Array.isArray(users) ? users : [];
  const grantableOptions = Array.isArray(roleOptions) ? roleOptions : [];
  const {
    page = 0,
    size = 25,
    totalCount = 0,
  } = pagination;

  const filtersActive = hasActiveCustomerFilters(listFilters);
  const [grandTotal, setGrandTotal] = useState(0);

  useEffect(() => {
    if (!filtersActive) {
      setGrandTotal(Number(totalCount) || 0);
    }
  }, [filtersActive, totalCount]);

  const resultSummary = buildUsersResultSummary(
    totalCount,
    filtersActive,
    grandTotal
  );

  const rows = useMemo(
    () =>
      usersList.map((user) => ({
        id: user.id ?? user.userId ?? user.username,
        email: user.email ?? "",
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        phone: user.phoneNumber ?? user.phone ?? "",
        accessVariant: deriveCustomerUserAccessVariant(user),
        sourceUser: user,
        accountStatus: user.accountStatus ?? "",
      })),
    [usersList]
  );

  const columns = useMemo(
    () => [
      {
        id: "email",
        label: headers[0],
        sortable: false,
        minWidth: 162,
        filter: { type: "text" },
        render: (value) => <EmailCellText>{displayCell(value)}</EmailCellText>,
      },
      {
        id: "firstName",
        label: headers[1],
        sortable: false,
        minWidth: 96,
        filter: { type: "text" },
        render: (value) => <MutedCellText>{displayCell(value)}</MutedCellText>,
      },
      {
        id: "lastName",
        label: headers[2],
        sortable: false,
        minWidth: 96,
        filter: { type: "text" },
        render: (value) => <MutedCellText>{displayCell(value)}</MutedCellText>,
      },
      {
        id: "phone",
        label: headers[3],
        sortable: false,
        minWidth: 122,
        filter: { type: "text" },
        render: (value) => {
          const formatted = formatDisplayPhone(value);
          return (
            <MonoCellText>{formatted ? formatted : "—"}</MonoCellText>
          );
        },
      },
      {
        id: "role",
        label: headers[4],
        sortable: false,
        minWidth: 166,
        filter: {
          type: "select",
          options: grantableOptions,
        },
        render: (_, row) => (
          <CustomerUserRolesField
            row={row}
            grantableOptions={grantableOptions}
            roleLabelMap={roleLabelMap}
            onAssignRoles={onAssignRoles}
          />
        ),
      },
      {
        id: "accessStatus",
        label: headers[5],
        sortable: false,
        minWidth: 154,
        filter: {
          type: "select",
          options: CUSTOMER_USERS_ACCESS_STATUS_FILTER_OPTIONS,
        },
        render: (_, row) => (
          <StatusPill
            label={row.accessVariant || "—"}
            tone={getMembershipStatusTone(row.accessVariant)}
          />
        ),
      },
      {
        id: "accountStatus",
        label: headers[6],
        sortable: false,
        minWidth: 128,
        render: (_, row) => {
          const label = normalizeAccountStatusDisplay(row.accountStatus);
          if (!label) {
            return "—";
          }
          return (
            <StatusPill label={label} tone={getAccountStatusTone(row.accountStatus)} />
          );
        },
      },
      {
        id: "action",
        label: headers[7],
        sortable: false,
        minWidth: 106,
        render: (_, row) => {
          const user = row.sourceUser;
          const rowAction = getCustomerUserListRowAction(user);
          if (!rowAction || typeof onRowAction !== "function") {
            return null;
          }
          return (
            <Box onClick={(event) => event.stopPropagation()}>
              <AccessActionButton
                type="button"
                $tone={rowAction.tone}
                onClick={() =>
                  onRowAction(
                    user,
                    rowAction.action,
                    (Array.isArray(user?.roles) ? user.roles : []).map(String)
                  )
                }
              >
                {rowAction.label}
              </AccessActionButton>
            </Box>
          );
        },
      },
    ],
    [
      headers,
      onAssignRoles,
      onRowAction,
      grantableOptions,
      roleLabelMap,
    ]
  );

  const tableFilterValues = useMemo(
    () => mapCustomerUsersListFiltersToColumnFilters(listFilters),
    [listFilters]
  );

  const emptyState = (
    <DataTableEmptyState
      title={USERS_PAGE_CONSTANTS.EMPTY_STATE_TITLE}
      hint={USERS_PAGE_CONSTANTS.EMPTY_STATE_HINT}
    />
  );

  return (
    <UsersListLayout>
      <ResultsBar>
        <ResultsSummary aria-live="polite" component="p">
          {resultSummary}
        </ResultsSummary>
        <ClearFiltersButton
          type="button"
          $active={filtersActive}
          disabled={!filtersActive}
          onClick={onClearFilters}
          data-testid="clear-filters-btn"
        >
          {USERS_PAGE_CONSTANTS.CLEAR_FILTERS_LABEL}
        </ClearFiltersButton>
      </ResultsBar>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        getRowId={(row) => row.id}
        showExport={false}
        hideToolbar
        serverMode
        page={page}
        totalRows={totalCount}
        defaultRowsPerPage={size}
        onPageChange={(nextPage) => fetchUsers(nextPage, size)}
        onRowsPerPageChange={(nextSize) => fetchUsers(0, nextSize)}
        onFilterChange={onListFiltersChange}
        filterValues={tableFilterValues}
        applyTextFiltersOnCommit
        testId="users-filter-table"
        renderEmptyState={() => emptyState}
      />
    </UsersListLayout>
  );
};

export default UsersList;

UsersList.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  users: PropTypes.array,
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    size: PropTypes.number,
    totalCount: PropTypes.number,
    totalPages: PropTypes.number,
    hasNext: PropTypes.bool,
    hasPrevious: PropTypes.bool,
  }),
  listFilters: PropTypes.object,
  onClearFilters: PropTypes.func,
  onListFiltersChange: PropTypes.func.isRequired,
  fetchUsers: PropTypes.func.isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.object),
  roleLabelMap: PropTypes.object,
  onAssignRoles: PropTypes.func.isRequired,
  onPatchCustomerAccess: PropTypes.func.isRequired,
  onRowAction: PropTypes.func.isRequired,
};
